import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';

const fetchWithAuth = (url, options = {}) => {
  const token = getToken();
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': token } });
};

const STATUS_LIST = ['全部', '进行中', '规划中', '已完成', '已暂停'];

const STATUS_CONFIG = {
  '进行中': { color: '#1890ff', bg: '#e6f7ff' },
  '规划中': { color: '#722ed1', bg: '#f9f0ff' },
  '已完成': { color: '#52c41a', bg: '#f6ffed' },
  '已暂停': { color: '#999', bg: '#f5f5f5' },
};

const TYPE_CONFIG = {
  '运维': { color: '#1890ff', bg: '#e6f7ff' },
  '实施': { color: '#fa8c16', bg: '#fff7e6' },
};

export default function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('全部');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [users, setUsers] = useState([]);
  const [newProject, setNewProject] = useState({ name: '', description: '', type: '运维', owner: '' });

  useEffect(() => { fetchProjects(); fetchUsers(); }, [filter]);

  const fetchUsers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/auth/users`);
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
    } catch (e) { console.error(e); }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const url = filter !== '全部' ? `${API_URL}/api/projects?status=${filter}` : `${API_URL}/api/projects`;
      const res = await fetchWithAuth(url);
      const data = await res.json();
      setProjects(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;
    try {
      await fetchWithAuth(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProject),
      });
      setShowCreate(false);
      setNewProject({ name: '', description: '', type: '运维', owner: '' });
      fetchProjects();
    } catch (e) { alert('创建失败'); }
  };

  const currentUser = localStorage.getItem('notes_user') || '';

  return (
    <div className="page-container">
      <div className="page-top-bar">
        <h1 className="page-title">📋 项目管理</h1>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ 新建项目</button>
      </div>

      <div className="filter-bar">
        {STATUS_LIST.map(s => (
          <button
            key={s}
            className={`filter-btn ${filter === s ? 'active' : ''}`}
            onClick={() => setFilter(s)}
          >{s}</button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">加载中...</div>
      ) : projects.length === 0 ? (
        <div className="empty-state">
          <p>暂无项目</p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>创建第一个项目</button>
        </div>
      ) : (
        <div className="project-grid">
          {projects.map(project => {
            const statusConf = STATUS_CONFIG[project.status] || STATUS_CONFIG['规划中'];
            const typeConf = TYPE_CONFIG[project.type] || TYPE_CONFIG['运维'];
            return (
              <div key={project.id} className="project-card" onClick={() => navigate(`/projects/${project.id}`)}>
                <div className="project-card-header">
                  <span className="project-type-tag" style={{ background: typeConf.bg, color: typeConf.color }}>
                    {project.type}
                  </span>
                  <span className="project-status-tag" style={{ background: statusConf.bg, color: statusConf.color }}>
                    {project.status}
                  </span>
                </div>
                <h3 className="project-card-name">{project.name}</h3>
                {project.description && <p className="project-card-desc">{project.description}</p>}
                <div className="project-card-meta">
                  <span>负责人：{users.find(u => u.username === project.owner)?.realname || project.owner}</span>
                  <span>任务：{project.completedCount || 0}/{project.taskCount || 0}</span>
                </div>
                <div className="project-progress-bar">
                  <div className="project-progress-fill" style={{ width: `${project.progress || 0}%` }} />
                </div>
                <span className="project-progress-text">{project.progress || 0}%</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 创建项目弹窗 */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-box modal-box-md" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">新建项目</h3>
            <form onSubmit={createProject}>
              <div className="form-group">
                <label>项目名称 *</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={e => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="请输入项目名称"
                  className="form-input"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>项目描述</label>
                <textarea
                  value={newProject.description}
                  onChange={e => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="请输入项目描述"
                  className="form-input"
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>项目类型</label>
                  <select
                    value={newProject.type}
                    onChange={e => setNewProject({ ...newProject, type: e.target.value })}
                    className="form-input"
                  >
                    <option value="运维">运维</option>
                    <option value="实施">实施</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>负责人</label>
                  <select
                    value={newProject.owner}
                    onChange={e => setNewProject({ ...newProject, owner: e.target.value })}
                    className="form-input"
                  >
                    <option value="">请选择</option>
                    {users.map(u => (
                      <option key={u.username} value={u.username}>{u.realname}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>取消</button>
                <button type="submit" className="btn-primary">创建</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
